import { useEffect, useRef, useState } from 'react';

/**
 * Hero motion.
 *
 * The video plays through exactly once while every frame is captured into an
 * offscreen canvas. When it ends the element is swapped for a display canvas
 * that ping-pongs the captured frames at 30fps, forward to the last frame and
 * back to the first, forever. No network loop, no seek stutter.
 */

const CLOUDFRONT_SRC =
  'https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260715_090628_7052d8a6-a094-4341-a4a2-ad58493a67a9.mp4';

/** Only used if the remote asset errors or stalls. Same pipeline either way. */
const LOCAL_FALLBACK_SRC = '/hero.mp4';

const MAX_CAPTURE_WIDTH = 960;
const FRAME_INTERVAL_MS = 1000 / 30;
const LOAD_TIMEOUT_MS = 8000;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface BoomerangVideoBgProps {
  /**
   * `fixed` pins the backdrop to the viewport so it sits behind the whole page
   * while the content scrolls over it; `absolute` confines it to its container.
   */
  position?: 'fixed' | 'absolute';
}

export function BoomerangVideoBg({ position = 'absolute' }: BoomerangVideoBgProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const framesRef = useRef<HTMLCanvasElement[]>([]);
  const [ready, setReady] = useState(false);
  const [src, setSrc] = useState<string>(CLOUDFRONT_SRC);
  const failedRef = useRef(false);

  // ── Capture pass ──────────────────────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const reduced = prefersReducedMotion();
    const frames: HTMLCanvasElement[] = [];
    framesRef.current = frames;

    let cancelled = false;
    let rafId = 0;
    let rvfcId = 0;
    let lastTime = -1;
    let loaded = false;

    const useRVFC =
      typeof (video as Partial<HTMLVideoElement>).requestVideoFrameCallback === 'function';

    const stopScheduling = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      if (rvfcId && typeof (video as Partial<HTMLVideoElement>).cancelVideoFrameCallback === 'function') {
        video.cancelVideoFrameCallback(rvfcId);
        rvfcId = 0;
      }
    };

    const capture = () => {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) return;
      // Deduplicate: the same presentation time never yields a second frame.
      if (video.currentTime === lastTime) return;
      lastTime = video.currentTime;

      const width = Math.min(MAX_CAPTURE_WIDTH, vw);
      const height = Math.max(1, Math.round((vh / vw) * width));
      const frame = document.createElement('canvas');
      frame.width = width;
      frame.height = height;
      const ctx = frame.getContext('2d');
      if (!ctx) return;
      try {
        ctx.drawImage(video, 0, 0, width, height);
      } catch {
        return;
      }
      frames.push(frame);
    };

    const finish = () => {
      if (cancelled) return;
      stopScheduling();
      if (frames.length > 0) setReady(true);
    };

    const step = () => {
      if (cancelled) return;
      capture();
      // Reduced motion: one still frame is the whole animation.
      if (reduced && frames.length > 0) {
        finish();
        return;
      }
      if (video.ended) {
        finish();
        return;
      }
      schedule();
    };

    function schedule() {
      if (cancelled) return;
      const el = videoRef.current;
      if (useRVFC && el) {
        rvfcId = el.requestVideoFrameCallback(step);
      } else {
        rafId = requestAnimationFrame(step);
      }
    }

    const fallBack = () => {
      if (failedRef.current) return;
      failedRef.current = true;
      cancelled = true;
      stopScheduling();
      setSrc(LOCAL_FALLBACK_SRC);
    };

    const timeoutId = window.setTimeout(() => {
      if (!loaded) fallBack();
    }, LOAD_TIMEOUT_MS);

    const onLoadedData = () => {
      loaded = true;
      window.clearTimeout(timeoutId);
      if (reduced) {
        // Draw a single poster frame and stop.
        video.currentTime = 0;
        capture();
        if (frames.length > 0) finish();
        else schedule();
        return;
      }
      const played = video.play();
      if (played && typeof played.catch === 'function') {
        played.catch(() => {
          /* Autoplay refused; frames stay empty and the video element remains. */
        });
      }
      schedule();
    };

    const onEnded = () => {
      capture();
      finish();
    };

    const onError = () => {
      window.clearTimeout(timeoutId);
      fallBack();
    };

    video.addEventListener('loadeddata', onLoadedData);
    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onError);

    if (video.readyState >= 2) onLoadedData();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      stopScheduling();
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onError);
    };
  }, [src]);

  // ── Ping-pong playback ────────────────────────────────────────────────────
  useEffect(() => {
    if (!ready) return;
    const canvas = canvasRef.current;
    const frames = framesRef.current;
    const first = frames[0];
    if (!canvas || !first) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = first.width;
    canvas.height = first.height;

    const draw = (index: number) => {
      const frame = frames[index];
      if (frame) ctx.drawImage(frame, 0, 0);
    };

    draw(0);

    if (prefersReducedMotion() || frames.length < 2) return;

    let index = 0;
    let direction = 1;
    const intervalId = window.setInterval(() => {
      index += direction;
      if (index >= frames.length - 1) {
        index = frames.length - 1;
        direction = -1;
      } else if (index <= 0) {
        index = 0;
        direction = 1;
      }
      draw(index);
    }, FRAME_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [ready]);

  return (
    <div className={`${position} inset-0 z-0`} aria-hidden="true">
      <div className="w-full h-full scale-[1.15] origin-top overflow-hidden">
        <video
          key={src}
          ref={videoRef}
          src={src}
          muted
          playsInline
          preload="auto"
          crossOrigin="anonymous"
          className="w-full h-full object-cover object-top"
          style={ready ? { display: 'none' } : undefined}
        />
        <canvas
          ref={canvasRef}
          className="w-full h-full object-cover object-top"
          style={ready ? undefined : { display: 'none' }}
        />
      </div>
    </div>
  );
}

export default BoomerangVideoBg;
